import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { PercentCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../Button';
import {
  getCroppedBlob,
  ImageSize,
  toNaturalCropArea,
} from './utils/crop-image';
import { rotateImageToDataUrl } from './utils/rotate-image';

type ImageCropModalProps = {
  /** Object URL da imagem selecionada. */
  imageSrc: string;
  /** Proporção com que a moldura abre. As alças podem mudá-la, só não a deitam. */
  initialAspect: number;
  /** Nome acessível do modal (ex.: "Recortar foto atualizada"). */
  label: string;
  onCancel: () => void;
  /** Recebe o recorte em JPEG. */
  onConfirm: (cropped: Blob) => void;
};

/** A imagem que está (ou vai estar) na tela e o giro que a gerou. */
type RotatedImage = { src: string; rotation: number };

/** A moldura abre com 90% do maior retângulo da proporção inicial que cabe. */
const INITIAL_CROP_RATIO = 0.9;
const MAX_FINE_ANGLE = 15;
/**
 * Tempo que o cursor de endireitar fica parado antes do redesenho. Cada grau
 * custa um canvas do tamanho natural da foto; sem a espera, arrastar o cursor
 * de ponta a ponta enfileira 31 redesenhos.
 */
const FINE_ANGLE_SETTLE_MS = 150;
/** 1× é a imagem inteira à vista; acima disso ela cresce e a caixa rola. */
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const OPEN_ERROR = 'Não foi possível abrir a imagem. Tente outro arquivo.';
const ROTATE_ERROR = 'Não foi possível preparar a imagem. Tente novamente.';
const CROP_ERROR = 'Não foi possível recortar a imagem. Tente novamente.';
const CROPPING_STATUS = 'Salvando a imagem…';

const RESIZE_HINT = 'Use as setas para redimensionar.';
const VERTICAL_RESIZE_HINT =
  'Use as setas para cima e para baixo para redimensionar.';
const HORIZONTAL_RESIZE_HINT =
  'Use as setas para a esquerda e para a direita para redimensionar.';

/** A `react-image-crop` traz estes rótulos em inglês e sem as teclas. */
const CROP_ARIA_LABELS = {
  cropArea: 'Área de recorte. Use as setas para mover.',
  nwDragHandle: `Alça do canto superior esquerdo. ${RESIZE_HINT}`,
  nDragHandle: `Alça da borda de cima. ${VERTICAL_RESIZE_HINT}`,
  neDragHandle: `Alça do canto superior direito. ${RESIZE_HINT}`,
  eDragHandle: `Alça da borda direita. ${HORIZONTAL_RESIZE_HINT}`,
  seDragHandle: `Alça do canto inferior direito. ${RESIZE_HINT}`,
  sDragHandle: `Alça da borda de baixo. ${VERTICAL_RESIZE_HINT}`,
  swDragHandle: `Alça do canto inferior esquerdo. ${RESIZE_HINT}`,
  wDragHandle: `Alça da borda esquerda. ${HORIZONTAL_RESIZE_HINT}`,
};

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), [tabindex]';

const secondaryButtonClass =
  'rounded-sm border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2';

/** `aria-disabled` não tira o foco do botão; só o aspecto muda. */
const inactiveButtonsClass =
  '[&_[aria-disabled=true]]:cursor-not-allowed [&_[aria-disabled=true]]:opacity-60';

/**
 * Impede a moldura de ficar deitada. A comparação é em pixels: cada eixo é uma
 * porcentagem de uma dimensão diferente.
 */
function keepPortrait(crop: PercentCrop, natural: ImageSize): PercentCrop {
  const widthPx = (crop.width / 100) * natural.width;
  const heightPx = (crop.height / 100) * natural.height;
  if (widthPx <= heightPx) return crop;

  return { ...crop, width: (heightPx / natural.width) * 100 };
}

/**
 * Maior retângulo de `aspect` que cabe em `width`×`height`, centrado e a 90%,
 * em porcentagem da imagem, para o zoom não deslocar a moldura.
 */
function cropFor(width: number, height: number, aspect: number): PercentCrop {
  const boxWidth = Math.min(width, height * aspect) * INITIAL_CROP_RATIO;
  const boxHeight = boxWidth / aspect;
  return {
    unit: '%',
    x: ((width - boxWidth) / 2 / width) * 100,
    y: ((height - boxHeight) / 2 / height) * 100,
    width: (boxWidth / width) * 100,
    height: (boxHeight / height) * 100,
  };
}

function describeZoom(zoom: number): string {
  if (zoom === 1) return '1 vez';
  return `${String(zoom).replace('.', ',')} vezes`;
}

/** Ângulo positivo gira no sentido horário (`ctx.rotate` com o eixo y para baixo). */
function describeAngle(degrees: number): string {
  if (degrees === 0) return 'Sem inclinação';
  const amount = Math.abs(degrees);
  const unit = amount === 1 ? 'grau' : 'graus';
  const direction = degrees > 0 ? 'horário' : 'anti-horário';
  return `${amount} ${unit} no sentido ${direction}`;
}

function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(element => element.tabIndex >= 0);
}

/** Mantém o Tab dentro do modal: do último volta ao primeiro, e vice-versa. */
function trapTab(event: KeyboardEvent, container: HTMLElement) {
  const focusable = focusableIn(container);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  const outside = !container.contains(active);

  if (event.shiftKey && (active === first || outside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || outside)) {
    event.preventDefault();
    first.focus();
  }
}

/** Enquanto o modal está aberto, o app atrás dele não recebe foco nem rola. */
function useInertApp() {
  useEffect(() => {
    const appRoot = document.getElementById('root');
    const { body } = document;
    const previousOverflow = body.style.overflow;
    // Só desfaz o `inert` que ele mesmo pôs.
    const setsInert = !!appRoot && !appRoot.hasAttribute('inert');
    if (setsInert) appRoot.setAttribute('inert', '');
    body.style.overflow = 'hidden';
    return () => {
      if (setsInert) appRoot.removeAttribute('inert');
      body.style.overflow = previousOverflow;
    };
  }, []);
}

/**
 * Modal de recorte com alças, zoom, giro de 90° e endireitar.
 *
 * A moldura abre em `initialAspect` e as alças levam-na a qualquer tamanho,
 * com uma única regra: nunca fica mais larga do que alta. O giro é aplicado
 * antes, numa imagem intermediária, para o recorte trabalhar sempre nas
 * coordenadas do que está na tela.
 */
export function ImageCropModal({
  imageSrc,
  initialAspect,
  label,
  onCancel,
  onConfirm,
}: ImageCropModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  /** Guarda síncrona: dois cliques no mesmo render não geram dois recortes. */
  const croppingRef = useRef(false);
  const [quarterTurns, setQuarterTurns] = useState(0);
  /** Valor do cursor: acompanha o polegar sem atraso. */
  const [fineAngle, setFineAngle] = useState(0);
  /** O que de fato entra no giro, só depois de o cursor parar. */
  const [settledFineAngle, setSettledFineAngle] = useState(0);
  const [rotated, setRotated] = useState<RotatedImage>({
    src: imageSrc,
    rotation: 0,
  });
  /** Giro da imagem que já carregou na tela; `null` antes do primeiro `load`. */
  const [loadedRotation, setLoadedRotation] = useState<number | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState<PercentCrop>();
  const [zoom, setZoom] = useState(MIN_ZOOM);
  /** Largura em que a imagem cabe na caixa; o zoom multiplica-a. */
  const [fitWidth, setFitWidth] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const rotation = quarterTurns * 90 + settledFineAngle;
  /**
   * O giro pedido conta já, antes de o cursor parar: Salvar não pode recortar
   * uma imagem sem o giro que a pessoa está a ver pedido.
   */
  const requestedRotation = quarterTurns * 90 + fineAngle;
  // A imagem na tela tem de ser a do giro pedido, e já carregada: num 90→180→90
  // rápido, a de 180 pode chegar depois de a pessoa voltar a pedir 90.
  const canSave =
    !isCropping &&
    !imageFailed &&
    rotated.rotation === requestedRotation &&
    loadedRotation === rotated.rotation;

  useInertApp();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog) focusableIn(dialog)[0]?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!croppingRef.current) onCancel();
      } else if (event.key === 'Tab') {
        trapTab(event, dialog);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Só o cursor espera. Os botões de 90° entram no giro na hora.
  useEffect(() => {
    const timer = setTimeout(
      () => setSettledFineAngle(fineAngle),
      FINE_ANGLE_SETTLE_MS,
    );
    return () => clearTimeout(timer);
  }, [fineAngle]);

  useEffect(() => {
    let cancelled = false;
    rotateImageToDataUrl(imageSrc, rotation)
      .then(src => {
        if (cancelled) return;
        // As três linhas ficam no mesmo lote de estado: o `src` novo nunca
        // chega ao DOM com o zoom antigo. `img.width` devolve a largura
        // desenhada; medi-la ampliada faria o zoom dobrar a cada giro.
        setZoom(MIN_ZOOM);
        setRotated({ src, rotation });
        setErrorMessage('');
      })
      .catch(() => {
        if (!cancelled) setErrorMessage(ROTATE_ERROR);
      });
    return () => {
      cancelled = true;
    };
  }, [imageSrc, rotation]);

  /** Cada imagem nova (original ou girada) reabre a moldura na proporção inicial. */
  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = event.currentTarget;
      setFitWidth(width);
      setCrop(cropFor(width, height, initialAspect));
      setLoadedRotation(rotated.rotation);
      setImageFailed(false);
    },
    [initialAspect, rotated.rotation],
  );

  function handleImageError() {
    setImageFailed(true);
    setErrorMessage(OPEN_ERROR);
  }

  function naturalSize(): ImageSize {
    const image = imageRef.current;
    return {
      width: image?.naturalWidth ?? 1,
      height: image?.naturalHeight ?? 1,
    };
  }

  function resetCrop() {
    const image = imageRef.current;
    if (!image) return;
    setCrop(cropFor(image.width, image.height, initialAspect));
  }

  function turn(delta: number) {
    setQuarterTurns(current => (current + delta + 4) % 4);
  }

  function handleCancel() {
    if (!croppingRef.current) onCancel();
  }

  async function handleConfirm() {
    const image = imageRef.current;
    if (!canSave || croppingRef.current) return;
    if (!image || !crop || crop.width <= 0 || crop.height <= 0) return;

    const area = toNaturalCropArea(crop, {
      width: image.naturalWidth,
      height: image.naturalHeight,
    });

    croppingRef.current = true;
    setIsCropping(true);
    let cropped: Blob;
    try {
      cropped = await getCroppedBlob(rotated.src, area);
    } catch {
      setErrorMessage(CROP_ERROR);
      return;
    } finally {
      croppingRef.current = false;
      setIsCropping(false);
    }

    // Fora do `try`: um erro do pai não é falha do canvas.
    onConfirm(cropped);
  }

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="flex w-full max-w-md flex-col gap-4 rounded-lg bg-white p-6 shadow-xl">
        {/* O `p-4` deixa as alças inteiras à vista na borda da imagem. */}
        <div className="flex max-h-80 justify-center overflow-auto rounded-md bg-gray-900 p-4">
          {/*
            O limite de altura fica no `ReactCrop`, não na imagem: a folha de
            estilo da biblioteca declara `max-height: inherit` na imagem, que
            vence qualquer classe posta nela. 18rem + o `p-4` = a caixa.
          */}
          <ReactCrop
            className="max-h-72"
            ariaLabels={CROP_ARIA_LABELS}
            crop={crop}
            onChange={(_pixelCrop, percentCrop) =>
              setCrop(keepPortrait(percentCrop, naturalSize()))
            }
          >
            <img
              ref={imageRef}
              src={rotated.src}
              alt="Imagem selecionada"
              onLoad={handleImageLoad}
              onError={handleImageError}
              // Só o estilo em linha vence o `max-width`/`max-height` da
              // biblioteca. A 1× o ajuste volta para a CSS.
              style={
                zoom > MIN_ZOOM && fitWidth > 0
                  ? {
                      width: fitWidth * zoom,
                      maxWidth: 'none',
                      maxHeight: 'none',
                    }
                  : undefined
              }
            />
          </ReactCrop>
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          {/* O glifo é o rótulo visível; o nome acessível vai no `aria-label`. */}
          <button
            type="button"
            onClick={() => turn(-1)}
            aria-label="Girar 90° à esquerda"
            className={secondaryButtonClass}
          >
            ↺ 90°
          </button>
          <button
            type="button"
            onClick={() => turn(1)}
            aria-label="Girar 90° à direita"
            className={secondaryButtonClass}
          >
            90° ↻
          </button>
          <button
            type="button"
            onClick={resetCrop}
            className={secondaryButtonClass}
          >
            Redefinir moldura
          </button>
        </div>

        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
          Zoom
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.1}
            value={zoom}
            aria-valuetext={describeZoom(zoom)}
            onChange={event => setZoom(Number(event.target.value))}
            className="w-full accent-[#000097]"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
          Endireitar
          <input
            type="range"
            min={-MAX_FINE_ANGLE}
            max={MAX_FINE_ANGLE}
            step={1}
            value={fineAngle}
            aria-valuetext={describeAngle(fineAngle)}
            onChange={event => setFineAngle(Number(event.target.value))}
            className="w-full accent-[#000097]"
          />
        </label>

        <div>
          <p role="status" className="sr-only">
            {isCropping ? CROPPING_STATUS : ''}
          </p>
          <p
            role="alert"
            className={`text-center text-sm text-red-700 ${
              errorMessage ? 'mb-3' : ''
            }`}
          >
            {errorMessage}
          </p>
          <div className={`grid grid-cols-2 gap-3 ${inactiveButtonsClass}`}>
            <button
              type="button"
              onClick={handleCancel}
              aria-disabled={isCropping}
              className={secondaryButtonClass}
            >
              Cancelar
            </button>
            <Button onClick={() => handleConfirm()} aria-disabled={!canSave}>
              Salvar
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
